export default class Socket {
    constructor({url, ...options} = {}) {
        this.url = null;
        this.options = {
            pingInterval: 60000,
            retryInterval: 3000,
            maxRetries: 20,
            ...options,
        };

        this.connectionId = null;
        this.connecting = false;
        this.connected = false;
        this.reconnectAttempts = 0;
        this.pingInterval = null;
        this.rootChannel = new Channel();
        this.channels = {};

        this.rootChannel.subscribe(this);
        this.on('open', this.handleOpen.bind(this));
        this.on('close', this.handleClose.bind(this));
        this.on('message', this.handleMessage.bind(this));
        this.on('init', this.handleInit.bind(this));
        this.on('reconnected', this.handleOpen.bind(this));

        if (url) {
            this.open(url);
        }
    }

    open(url) {
        this.url = url;
        this.connecting = true;

        if (this.connectionId !== null) {
            url += `?connection_id=${this.connectionId}`;
        }

        this.ws = new WebSocket(url);
        this.ws.onopen = event => this.receive(this.reconnectAttempts === 0 ? 'open' : 'reconnected', event);
        this.ws.onmessage = event => this.receive('message', event);
        this.ws.onclose = event => this.receive('close', event);
        this.ws.onerror = event => this.receive('error', event);
    }

    handleOpen() {
        this.reconnectAttempts = 0;
        this.connecting = false;
        this.connected = true;
        this.pingInterval = setInterval(() => {
            this.emit('ping');
        }, this.options.pingInterval);
    }

    handleClose() {
        clearInterval(this.pingInterval);

        if (this.connecting || this.connected) {
            if (this.reconnectAttempts >= this.options.maxRetries) {
                this.connecting = false;
                this.close();
                this.receive('error', 'RECONNECT_FAILED');
            } else {
                if (this.reconnectAttempts === 0) {
                    this.receive('reconnecting');
                }
                this.connecting = true;
                setTimeout(() => {
                    this.reconnectAttempts++;
                    this.open(this.url);
                }, this.options.retryInterval);
            }
        } else {
            this.initialized = false;
            this.connectionId = null;
            this.receive('disconnected');
        }
    }

    handleMessage(event) {
        let data = this.parseMessage(event.data);
        let channel = data.hasOwnProperty('channel_id') ? this.getChannel(data.channel_id) : this.rootChannel;
        channel.receive(data.signal, data);
    }

    parseMessage(raw) {
        try {
            return JSON.parse(raw);
        } catch (e) {
            throw "Websocket event could not be parsed as JSON.";
        }
    }

    getChannel(channelId) {
        if (!this.channels.hasOwnProperty(channelId)) {
            throw `unknown channel '${channelId}'.`;
        }
        return this.channels[channelId];
    }

    handleInit(data) {
        if (!data.hasOwnProperty('connection_id')) {
            throw 'Misses connection_id in socket init event.';
        }
        this.connectionId = data.connection_id;
        this.receive('connected');
    }

    subscribe(name, payload = {}) {
        let channel = new Channel(name, payload);
        channel.subscribe(this);
        return channel;
    }

    unsubscribe(channelId) {
        this.getChannel(channelId).unsubscribe();
    }

    attachChannel(channel) {
        this.channels[channel.channelId] = channel;
    }

    detachChannel(channelId) {
        delete this.channels[channelId];
    }

    on(signal, callback) {
        return this.rootChannel.on(signal, callback);
    }

    once(signal, callback) {
        return this.rootChannel.once(signal, callback);
    }

    off(signal, callback) {
        return this.rootChannel.on(signal, callback);
    }

    receive(signal, payload = {}) {
        return this.rootChannel.receive(signal, payload);
    }

    emit(signal, payload = {}) {
        return this.rootChannel.emit(signal, payload);
    }

    close() {
        this.connected = false;
        this.ws.close();
        Object.values(this.channels).forEach(channel => channel.receive('close'));
    }
}

class Channel {
    constructor(name = null, payload = {}) {
        this.isRoot = name === null;
        this.name = name;
        this.payload = payload;
        this.socket = null;
        this.channelId = null;
        this.signalHandler = {};
        this.initialized = false;

        this.on('init', () => {
            this.initialized = true;
        });
        this.on('close', () => {
            if (!this.isRoot) {
                this.socket.detachChannel(this.channelId);
                this.channelId = null;
            }
        });
    }

    subscribe(socket) {
        if (!this.socket && !this.isRoot) {
            socket.on('reconnecting', () => {
                this.receive('close');
            });
            socket.on('init', () => {
                this.subscribe(this.socket);
            });
        }
        this.socket = socket;

        if (!this.isRoot) {
            this.socket.emit('subscribe', this.generatePayload(null, this.payload));
            socket.attachChannel(this);
        }
    }

    unsubscribe() {
        this.socket.emit('unsubscribe', this.generatePayload());
        this.receive('close');
    }

    on(signal, callback) {
        if (!this.signalHandler.hasOwnProperty(signal)) {
            this.signalHandler[signal] = [];
        }
        this.signalHandler[signal].push(callback);
        return () => this.off(signal, callback);
    }

    once(signal, callback) {
        let onceCallback;
        let onceOff = () => this.off(signal, onceCallback);

        onceCallback = (...payload) => {
            callback(...payload);
            setTimeout(onceOff, 0); // on next tick on purpose
        };

        // discard once handler on close/reconnect to prevent loop
        this.socket.on('disconnected', onceOff);
        this.socket.on('reconnecting', onceOff);

        this.on(signal, onceCallback);
        return onceOff;
    }

    off(signal, callback) {
        if (!this.signalHandler.hasOwnProperty(signal)) {
            return;
        }
        if (callback instanceof Function) {
            let index = this.signalHandler[signal].indexOf(callback);
            if (~index) {
                this.signalHandler[signal].splice(index, 1);
            }
        } else {
            this.signalHandler[signal] = [];
        }
    }

    async receive(signal, payload = {}) {
        if (!this.signalHandler.hasOwnProperty(signal)) {
            return;
        }
        for (let callback of this.signalHandler[signal]) {
            await callback(payload);
        }
    }

    emit(signal, payload = {}) {
        if (!this.initialized) { // delay emit until channel initialized
            this.once('init', () => this.emit(signal, payload));
            return;
        }
        this.socket.ws.send(JSON.stringify(this.generatePayload(signal, payload)));
    }

    generatePayload(signal = null, payload = {}) {
        payload = {...payload}; // clone
        if (signal !== null) {
            payload.signal = signal;
        }
        if (!this.isRoot) {
            if (this.channelId === null) {
                this.channelId = `${this.name}_${this.socket.connectionId}_${+new Date()}`;
            }
            payload.channel_id = this.channelId;
            payload.channel = this.name;
        }
        return payload;
    }
}